import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Lock, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PropertyDeleteSectionProps {
  wasSoldOnLoad?: boolean;
  onDelete: () => void;
}

export const PropertyDeleteSection = ({ wasSoldOnLoad = false, onDelete }: PropertyDeleteSectionProps) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [showAdminDeleteConfirmation, setShowAdminDeleteConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsAdmin(false);
          setIsCheckingAdmin(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_roles" as any)
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .single();

        setIsAdmin(!error && !!data);
      } catch {
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  const handleDeleteClick = () => {
    if (wasSoldOnLoad && isAdmin) {
      setShowAdminDeleteConfirmation(true);
    } else {
      setShowDeleteConfirmation(true);
    }
  };

  const handleConfirmAdminDelete = () => {
    setShowAdminDeleteConfirmation(false);
    onDelete();
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirmation(false);
    onDelete();
  };

  // Locked state for non-admin users on sold properties
  const isLockedForSeller = wasSoldOnLoad && !isAdmin && !isCheckingAdmin;

  return (
    <>
      <Card className="mt-6 border-red-500/20">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-red-500">Danger Zone</h2>
            <Separator className="bg-red-500/20" />
            
            {isLockedForSeller ? (
              <div className="flex items-start gap-3 p-4 bg-muted/50 border border-border rounded-md">
                <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Deletion Disabled</p>
                  <p className="text-sm text-muted-foreground">
                    Sold properties cannot be deleted. They serve as market comparables for buyers 
                    and are part of the platform's historical data.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {wasSoldOnLoad && isAdmin && (
                  <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-md mb-4">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Admin Delete Warning</p>
                      <p className="text-sm text-muted-foreground">
                        This sold property contains historical market data. Deleting it will permanently 
                        remove this comparable data from the platform.
                      </p>
                    </div>
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. This will permanently delete this property listing from your account.
                </p>
                <Button
                  type="button"
                  variant="destructive"
                  className="mt-2"
                  onClick={handleDeleteClick}
                >
                  Delete Property
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin delete confirmation for sold properties */}
      <AlertDialog open={showAdminDeleteConfirmation} onOpenChange={setShowAdminDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Sold Property
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete a sold property. This will remove historical market 
              comparable data that buyers rely on. This action cannot be undone. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAdminDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete Property
            </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Standard delete confirmation */}
    <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Property
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this property? This action cannot be undone 
            and will permanently remove this listing from your account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmDelete}
            className="bg-destructive hover:bg-destructive/90"
          >
            Delete Property
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
);
};
